import React, { useState, useRef } from "react";
import Slider from "react-slick";
import { FaArrowLeft, FaArrowRight, FaChevronRight, FaStar } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { heroSlides, sliderSettings, heroConfig } from "../../data/heroData";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function NextArrow({ onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      className="absolute z-30 flex items-center justify-center w-10 h-10 transition-all -translate-y-1/2 border rounded-full right-4 top-1/2 bg-white/20 backdrop-blur-sm border-white/30 hover:bg-white/30"
      aria-label="Next slide"
    >
      <FaArrowRight size={16} className="text-white" />
    </motion.button>
  );
}

function PrevArrow({ onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      className="absolute z-30 flex items-center justify-center w-10 h-10 transition-all -translate-y-1/2 border rounded-full left-4 top-1/2 bg-white/20 backdrop-blur-sm border-white/30 hover:bg-white/30"
      aria-label="Previous slide"
    >
      <FaArrowLeft size={16} className="text-white" />
    </motion.button>
  );
}

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  const settings = {
    ...sliderSettings,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    appendDots: (dots) => (
      <div className="absolute z-30 w-full bottom-6">
        <ul className="flex justify-center gap-2">{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <button className="focus:outline-none">
        <div className="relative">
          <div className={`h-1.5 rounded-full transition-all duration-300 ${
            currentSlide === i 
              ? 'w-10 bg-white' 
              : 'w-6 bg-white/50 hover:bg-white/70'
          }`}>
            {currentSlide === i && (
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-blue-400 to-cyan-300"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: heroConfig.progressBarDuration, ease: "linear" }}
              />
            )}
          </div>
        </div>
      </button>
    ),
  };

  const goToSlide = (index) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  const handleButtonClick = (link) => {
    // Handle navigation or external links here
    console.log("Navigating to:", link);
  };

  return (
    <section className="relative w-full overflow-hidden">
      <Slider ref={sliderRef} {...settings}>
        {heroSlides.map((slide, index) => (
          <div key={slide.id}>
            <div className={`relative h-[${heroConfig.height.mobile}] md:h-[${heroConfig.height.desktop}] py-10 overflow-hidden`}>
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt="School"
                  className="object-cover w-full h-full"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-r ${slide.overlayColor}`} />
                {/* Additional overlay */}
                <div className="absolute inset-0 bg-black/30" />
              </div>

              {/* Content Container */}
              <div className="relative flex items-center h-full">
                <div className="container px-4 mx-auto sm:px-6">
                  <div className="max-w-2xl">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={slide.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: heroConfig.animationDuration }}
                        className="space-y-5"
                      >
                        {/* Rating Badge */}
                        {slide.ratingText && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 }}
                            className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/20"
                          >
                            <div className="flex items-center gap-0.5">
                              {[...Array(heroConfig.ratingStars)].map((_, i) => (
                                <FaStar key={i} className="w-2.5 h-2.5 text-amber-300" />
                              ))}
                            </div>
                            <span className="text-xs font-medium text-white">
                              {slide.ratingText}
                            </span>
                          </motion.div>
                        )}

                        {/* Main Heading */}
                        <motion.h1
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="text-3xl font-bold leading-snug text-white md:text-4xl lg:text-5xl"
                        >
                          {slide.heading}{" "}
                          <span className={`bg-gradient-to-r ${slide.accentColor} bg-clip-text text-transparent`}>
                            {slide.highlight}
                          </span>
                        </motion.h1>

                        {/* Description */}
                        <motion.p
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="max-w-xl text-base leading-relaxed md:text-lg text-white/90"
                        >
                          {slide.sub}
                        </motion.p>

                        {/* Stats */}
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="flex flex-wrap gap-4 pt-2"
                        >
                          {slide.stats.map((stat, statIndex) => (
                            <div key={statIndex} className="text-center">
                              <div className={`text-xl font-bold bg-gradient-to-r ${slide.accentColor} bg-clip-text text-transparent`}>
                                {stat.value}
                              </div>
                              <div className="text-xs font-medium text-white/70">{stat.label}</div>
                            </div>
                          ))}
                        </motion.div>

                        {/* CTA Buttons */}
                        <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                          className="flex flex-col gap-3 pt-3 sm:flex-row"
                        >
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => handleButtonClick(slide.buttonLink)}
                            className={`px-6 py-2.5 bg-gradient-to-r ${slide.accentColor} text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-1.5 text-sm`}
                          >
                            {slide.buttonText}
                            <FaChevronRight className="group-hover:translate-x-0.5 transition-transform text-sm" />
                          </motion.button>
                          
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => handleButtonClick(slide.secondaryButtonLink)}
                            className="px-6 py-2.5 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg border border-white/30 hover:bg-white/15 transition-all duration-200 text-sm"
                          >
                            {slide.secondaryButtonText}
                          </motion.button>
                        </motion.div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Slide Number Indicator */}
              <div className="absolute px-3 py-1 rounded-full top-4 right-4 bg-black/30 backdrop-blur-sm">
                <span className="text-sm font-medium text-white">
                  <span className="text-lg">{currentSlide + 1}</span>
                  <span className="mx-1 text-white/60">/</span>
                  <span className="text-base text-white/80">{heroSlides.length}</span>
                </span>
              </div>
            </div>
          </div>
        ))}
      </Slider>

      {/* Simple Dots Navigation */}
      <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1.5">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-200 ${
              currentSlide === index 
                ? 'w-8 bg-white' 
                : 'w-2 bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;